import React, { useState, useRef, useEffect } from "react";
import {
  TrendingUp,
  Brain,
  BookOpen,
  Pill,
  Database,
  Share,
  Copy,
  Check,
} from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import { useSearchParams } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db, auth } from "../../utils/firebase";
import { signInAnonymously } from "firebase/auth";
import RCPublicOverview from "./RCPublicOverview";
import RCQuickGlance from "./RCQuickGlance";
import RCProgressionTab from "./RCProgressionTab";
import RCMATAnalysisTab from "./RCMATAnalysisTab";
import RCNotesTab from "./RCHypothesesTab";
import RCKeyFactorsTab from "./RCKeyFactorsTab";
import RCProbationTab from "./RCProbationTab";

const RecoveryCapitalAnalytics = () => {
  const {
    user,
    isLoading,
    isAuthenticated, // Add this from useAuth
    organizationId: authOrgId,
    organizationData,
  } = useAuth();
  const [activeTab, setActiveTab] = useState("quickGlance");
  const [searchParams] = useSearchParams();
  const [publicOrgId, setPublicOrgId] = useState(null);
  const [publicAccessLoading, setPublicAccessLoading] = useState(false);
  const [publicAccessError, setPublicAccessError] = useState(null);

  // Link generation states
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const publicToken = searchParams.get("token");
  const isPublicView = !!publicToken;

  // Function to generate a random token
  const generateToken = () => {
    return (
      Math.random().toString(36).substring(2) +
      Math.random().toString(36).substring(2) +
      Date.now().toString(36)
    );
  };

  // Function to generate public access link
  const generatePublicLink = async () => {
    if (!authOrgId) return;

    try {
      setIsGeneratingLink(true);

      // Generate a unique token
      const token = generateToken();

      // Create the public access document in Firestore
      await setDoc(doc(db, "publicAccessTokens", token), {
        organizationId: authOrgId,
        createdAt: new Date(),
        createdBy: user?.email || user?.uid,
        // Optional: Add expiration date (e.g., 1 year from now)
        // expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      });

      // Generate the full URL
      const baseUrl = window.location.origin;
      const publicUrl = `${baseUrl}/analytics-public?token=${token}`;

      setGeneratedLink(publicUrl);
    } catch (error) {
      console.error("Error generating public link:", error);
      alert("Failed to generate public link. Please try again.");
    } finally {
      setIsGeneratingLink(false);
    }
  };

  // Function to copy link to clipboard
  const copyToClipboard = async () => {
    if (!generatedLink) return;

    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = generatedLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Function to verify public access token
  const verifyPublicAccess = async (token) => {
    if (!token) return null;

    try {
      setPublicAccessLoading(true);
      const tokenDoc = await getDoc(doc(db, "publicAccessTokens", token));

      if (tokenDoc.exists()) {
        const data = tokenDoc.data();
        // Check if token has expired (optional)
        if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
          throw new Error("Access token has expired");
        }
        return data.organizationId;
      } else {
        throw new Error("Invalid access token");
      }
    } catch (error) {
      console.error("Error verifying public access:", error);
      setPublicAccessError("Invalid or expired access link");
      return null;
    } finally {
      setPublicAccessLoading(false);
    }
  };

  // ADD THIS NEW EFFECT - This is the key fix!
  useEffect(() => {
    const handlePublicAccess = async () => {
      if (publicToken && !isAuthenticated) {
        try {
          // First verify the token exists and is valid
          const tokenDoc = await getDoc(
            doc(db, "publicAccessTokens", publicToken)
          );

          if (!tokenDoc.exists()) {
            throw new Error("Invalid access token");
          }

          const tokenData = tokenDoc.data();

          // Check if token has expired (optional)
          if (
            tokenData.expiresAt &&
            tokenData.expiresAt.toDate() < new Date()
          ) {
            throw new Error("Access token has expired");
          }

          // Sign in anonymously to satisfy Firestore auth requirements
          await signInAnonymously(auth);

          // Set the organization ID from the token
          setPublicOrgId(tokenData.organizationId);
        } catch (error) {
          console.error("Error with public access:", error);
          setPublicAccessError("Invalid or expired access link");
        }
      }
    };

    if (publicToken && !isAuthenticated) {
      handlePublicAccess();
    }
  }, [publicToken, isAuthenticated]);

  // Effect to handle public token verification (keep this but it's now redundant)
  useEffect(() => {
    if (publicToken && isAuthenticated && !authOrgId) {
      verifyPublicAccess(publicToken).then(setPublicOrgId);
    }
  }, [publicToken, isAuthenticated, authOrgId]);

  // Determine which organization ID to use
  const effectiveOrgId = authOrgId || publicOrgId;
  const effectiveLoading = isLoading || (isPublicView && publicAccessLoading);

  // Loading state
  if (effectiveLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Error state for public access
  if (isPublicView && publicAccessError) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Access Error: </strong>
        <span className="block sm:inline">{publicAccessError}</span>
      </div>
    );
  }

  // No organization ID available
  if (!effectiveOrgId) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">
          {isPublicView ? "Invalid access link" : "Organization ID is required"}
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-950 p-6 relative overflow-visible">
      {/* Ambient background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Header with title */}
        <div className="mb-12 border-b border-slate-700 pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex gap-3">
                <div className="h-16 w-1 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full animate-pulse" />
                <div className="h-16 w-1 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full animate-pulse delay-100 opacity-70" />
                <div className="h-16 w-1 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full animate-pulse delay-200 opacity-40" />
              </div>
              <div>
                <h1 className="text-5xl font-bold text-white tracking-tight font-serif">
                  Recovery Capital Analytics
                </h1>
                <p className="text-slate-400 mt-2 text-xl font-light">
                  Data-Driven Insights | 2025
                  {isPublicView && (
                    <span className="ml-2 text-sm bg-blue-600/20 text-blue-300 px-2 py-1 rounded">
                      Public View
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Public Link Generator - Only show for authenticated users */}
            {!isPublicView && authOrgId && (
              <div className="flex flex-col gap-3">
                <button
                  onClick={generatePublicLink}
                  disabled={isGeneratingLink}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600/20 hover:bg-green-600/30 border border-green-500/50 text-green-300 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Share size={16} />
                  {isGeneratingLink ? "Generating..." : "Generate Public Link"}
                </button>

                {generatedLink && (
                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 max-w-md">
                    <p className="text-sm text-slate-400 mb-2">
                      Public Dashboard Link:
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={generatedLink}
                        readOnly
                        className="flex-1 bg-slate-900/50 border border-slate-600 rounded px-2 py-1 text-sm text-slate-300 font-mono"
                      />
                      <button
                        onClick={copyToClipboard}
                        className="flex items-center gap-1 px-3 py-1 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/50 text-blue-300 rounded transition-all duration-200"
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        {copied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Share this link with providers to give them access to your
                      dashboard
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8 flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => setActiveTab("quickGlance")}
            className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 ${
              activeTab === "quickGlance"
                ? "bg-cyan-900/50 text-cyan-300 border border-cyan-700/50"
                : "bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-800"
            }`}
          >
            <Database size={16} />
            <span>Quick Glance</span>
          </button>
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 ${
              activeTab === "overview"
                ? "bg-purple-900/50 text-purple-300 border border-purple-700/50"
                : "bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-800"
            }`}
          >
            <Database size={16} />
            <span>Overview</span>
          </button>
          <button
            onClick={() => setActiveTab("progression")}
            className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 ${
              activeTab === "progression"
                ? "bg-blue-900/50 text-blue-300 border border-blue-700/50"
                : "bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-800"
            }`}
          >
            <TrendingUp size={16} />
            <span>Progression</span>
          </button>
          <button
            onClick={() => setActiveTab("factors")}
            className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 ${
              activeTab === "factors"
                ? "bg-emerald-900/50 text-emerald-300 border border-emerald-700/50"
                : "bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-800"
            }`}
          >
            <Brain size={16} />
            <span>Key Factors</span>
          </button>
          <button
            onClick={() => setActiveTab("mat")}
            className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 ${
              activeTab === "mat"
                ? "bg-amber-900/50 text-amber-300 border border-amber-700/50"
                : "bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-800"
            }`}
          >
            <Pill size={16} />
            <span>MAT Analysis</span>
          </button>
          <button
            onClick={() => setActiveTab("probation")}
            className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 ${
              activeTab === "probation"
                ? "bg-orange-900/50 text-orange-300 border border-orange-700/50"
                : "bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-800"
            }`}
          >
            <BookOpen size={16} />
            <span>Probation/Parole</span>
          </button>
          <button
            onClick={() => setActiveTab("hypotheses")}
            className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center gap-2 ${
              activeTab === "hypotheses"
                ? "bg-pink-900/50 text-pink-300 border border-pink-700/50"
                : "bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-800"
            }`}
          >
            <BookOpen size={16} />
            <span>Thoughts/Plans</span>
          </button>
        </div>

        {activeTab === "quickGlance" && (
          <RCQuickGlance organizationId={effectiveOrgId} />
        )}

        {/* Data Overview Panel */}
        {activeTab === "overview" && (
          <RCPublicOverview organizationId={effectiveOrgId} />
        )}

        {activeTab === "progression" && (
          <RCProgressionTab organizationId={effectiveOrgId} />
        )}

        {/* Key Factors Panel */}
        {activeTab === "factors" && (
          <RCKeyFactorsTab organizationId={effectiveOrgId} />
        )}

        {/* MAT Analysis Panel */}
        {activeTab === "mat" && (
          <RCMATAnalysisTab organizationId={effectiveOrgId} />
        )}

        {activeTab === "probation" && (
          <RCProbationTab organizationId={effectiveOrgId} />
        )}

        {activeTab === "hypotheses" && (
          <RCNotesTab
            organizationId={effectiveOrgId}
            isPublicView={isPublicView}
          />
        )}
      </div>
    </div>
  );
};

export default RecoveryCapitalAnalytics;
